import { useCallback, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Badge, Button, Card, CardBody, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap"

import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import InputField from "components/Common/InputField"
import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { ACCESS } from "constants/accessPermissions"
import { useCreateLeaveType, useDeleteLeaveType, useEditLeaveType, useLeaveTypes } from "hooks/leaveHook"
import { useListParams } from "hooks/useListParams"

const LeaveTypeList = () => {
  document.title = "Leave Types"

  const { params, updateParams: setParams } = useListParams({ page: 1, per_page: 15 })
  const [search, setSearch] = useState("")
  const [modal, setModal] = useState({ open: false, mode: "create", type: null })
  const [form, setForm] = useState({ name: "", slug: "", days_per_year: "", is_paid: false, requires_approval: true, is_active: true })
  const [deleteId, setDeleteId] = useState(null)

  const { data, isLoading, refetch } = useLeaveTypes({ search })
  const types = data?.data ?? []
  const createMutation = useCreateLeaveType()
  const editMutation = useEditLeaveType()
  const deleteMutation = useDeleteLeaveType()

  const openCreate = () => {
    setForm({ name: "", slug: "", days_per_year: "", is_paid: false, requires_approval: true, is_active: true })
    setModal({ open: true, mode: "create", type: null })
  }

  const openEdit = type => {
    setForm({
      name: type.name,
      slug: type.slug,
      days_per_year: type.days_per_year,
      is_paid: Boolean(type.is_paid),
      requires_approval: Boolean(type.requires_approval),
      is_active: Boolean(type.is_active),
    })
    setModal({ open: true, mode: "edit", type })
  }

  const handleSubmit = async () => {
    try {
      if (modal.mode === "create") {
        await createMutation.mutateAsync(form)
        toast.success("Leave type created")
      } else {
        await editMutation.mutateAsync({ id: modal.type.id, ...form })
        toast.success("Leave type updated")
      }
      setModal({ open: false, mode: "create", type: null })
      refetch()
    } catch (err) {
      toast.error(err?.response?.data?.message || "Operation failed")
    }
  }

  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(deleteId)
      toast.success("Leave type deleted")
      setDeleteId(null)
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed")
    }
  }

  const columns = useMemo(
    () => [
      { id: "name", Header: "Name", accessor: "name" },
      { id: "slug", Header: "Slug", accessor: "slug" },
      { id: "days_per_year", Header: "Days/Year", accessor: "days_per_year" },
      {
        id: "is_paid",
        Header: "Paid",
        accessor: row => (row.is_paid ? <Badge color="success">Yes</Badge> : <Badge color="secondary">No</Badge>),
      },
      {
        id: "is_active",
        Header: "Active",
        accessor: row => (row.is_active ? <Badge color="success">Active</Badge> : <Badge color="danger">Inactive</Badge>),
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: row => (
          <div className="d-flex gap-1">
            <Button size="sm" color="primary" onClick={() => openEdit(row)}>
              Edit
            </Button>
            <Button size="sm" color="danger" onClick={() => setDeleteId(row.id)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <PageWrapper title="Leave Types">
      <Card>
        <CardBody>
          <Row className="mb-3">
            <Col md={4}>
              <GlobalInputSearch value={search} onChange={setSearch} placeholder="Search..." />
            </Col>
            <Col className="text-end">
              <Button color="primary" onClick={openCreate}>
                Add Leave Type
              </Button>
            </Col>
          </Row>
          <TableContainer columns={columns} data={types} isLoading={isLoading} onRefresh={refetch} />
        </CardBody>
      </Card>

      <Modal isOpen={modal.open} toggle={() => setModal({ open: false, mode: "create", type: null })}>
        <ModalHeader>{modal.mode === "create" ? "Add" : "Edit"} Leave Type</ModalHeader>
        <ModalBody>
          <InputField label="Name" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
          <InputField label="Slug" value={form.slug} onChange={v => setForm(f => ({ ...f, slug: v }))} disabled={modal.mode === "edit"} />
          <InputField label="Days Per Year" type="number" value={form.days_per_year} onChange={v => setForm(f => ({ ...f, days_per_year: v }))} />
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="is_paid"
              checked={form.is_paid}
              onChange={e => setForm(f => ({ ...f, is_paid: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="is_paid">Paid Leave</label>
          </div>
          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="requires_approval"
              checked={form.requires_approval}
              onChange={e => setForm(f => ({ ...f, requires_approval: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="requires_approval">Requires Approval</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor="is_active">Active</label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit} disabled={createMutation.isPending || editMutation.isPending}>
            Save
          </Button>
          <Button color="secondary" onClick={() => setModal({ open: false, mode: "create", type: null })}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <DeleteModal
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </PageWrapper>
  )
}

export default LeaveTypeList